import { useState, useCallback, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import {
  Box,
  Text,
  IconButton,
  Icon,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Button,
  chakra,
  Tooltip,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { AddIcon } from '@chakra-ui/icons';
import { Pencil, Trash, ChevronDown, ChevronUp, GripVerticalIcon } from 'lucide-react';
import { Reorder, useDragControls } from 'framer-motion';

import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { trpcAdmin } from '../../../lib/trpcAdmin';

import { unitAssessmentState } from '../../UnitAssessmentState';
import { UnitAssessmentSlides } from '../../types';
import type { FormFieldType, FormSectionType } from './types';
import { FormField } from './FormField';

const FieldList = chakra(Reorder.Group<FormFieldType>);
const SectionList = chakra(Reorder.Group<FormSectionType>);
const SectionItem = chakra(Reorder.Item);

type FormSectionAccordionProps = {
  sections: FormSectionType[];
  slide: UnitAssessmentSlides[0];
  setCurrentSlide: (slide: UnitAssessmentSlides[0]) => void;
};

type SectionProps = {
  section: FormSectionType;
  slide: UnitAssessmentSlides[0];
  setCurrentSlide: (slide: UnitAssessmentSlides[0]) => void;
  onDragEnd: () => void;
  onDragStart: () => void;
};

function Section({ section, slide, setCurrentSlide, onDragEnd, onDragStart }: SectionProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [localFieldsOrder, setLocalFieldsOrder] = useState<FormFieldType[]>(section.fields || []);
  const sectionDragControls = useDragControls();

  const utils = trpcAdmin.useUtils();

  const deleteFormSectionMutation = trpcAdmin.unitAssessment.deleteFormSection.useMutation({
    onSuccess: async () => {
      unitAssessmentState.setHasUnpublishedChanges(true);
      await utils.unitAssessment.getSlides.invalidate();
    },
  });

  const reorderFieldsMutation = trpcAdmin.unitAssessment.reorderField.useMutation({
    onSuccess: async () => {
      unitAssessmentState.setHasUnpublishedChanges(true);
      await utils.unitAssessment.getSlides.invalidate();
    },
  });

  const handleEditSection = useCallback(() => {
    setCurrentSlide(slide);
    unitAssessmentState.setCurrentFormSectionId(section.id);
    unitAssessmentState.setEditorType('FORM_SECTION');
    unitAssessmentState.setEditorMode(EditorMode.EDIT);
    unitAssessmentState.openEditor();
  }, [section.id, slide, setCurrentSlide]);

  const handleDeleteSection = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    deleteFormSectionMutation.mutate(section.id);
    setIsDeleteDialogOpen(false);
    unitAssessmentState.closeEditor();
  }, [deleteFormSectionMutation, section.id]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, []);

  const handleAddField = useCallback(() => {
    setCurrentSlide(slide);
    unitAssessmentState.setCurrentFormSectionId(section.id);
    unitAssessmentState.setEditorType('FORM_FIELD');
    unitAssessmentState.setEditorMode(EditorMode.CREATE);
    unitAssessmentState.setCurrentFormFieldId(null);
    unitAssessmentState.openEditor();
  }, [section.id, slide, setCurrentSlide]);

  const handleLocalReorder = useCallback((newOrder: FormFieldType[]) => {
    setLocalFieldsOrder(newOrder);
  }, []);

  const handleItemDragEnd = useCallback(
    (fieldId: string) => {
      const updatedOrder = localFieldsOrder;

      // Find the index of the dragged item in the updated list + 1 for position
      const targetIndex = updatedOrder.findIndex(({ id }) => id === fieldId);
      if (targetIndex !== -1) {
        reorderFieldsMutation.mutate({
          fieldId,
          targetPosition: targetIndex + 1,
          sectionId: section.id,
        });
      }
    },
    [localFieldsOrder, reorderFieldsMutation, section.id],
  );

  // Update local fields when section.fields changes
  useEffect(() => {
    setLocalFieldsOrder(section.fields || []);
  }, [section.fields]);

  return (
    <SectionItem
      value={section}
      key={section.id}
      dragListener={false}
      dragControls={sectionDragControls}
      onDragEnd={onDragEnd}
      border="none"
      mb={2}
    >
      <AccordionItem border="none">
        {({ isExpanded }) => (
          <Box borderWidth="1px" borderRadius="md" bg="bg.subtle" overflow="hidden">
            <HStack justify="space-between" p={3}>
              <HStack>
                <Text fontWeight="bold">
                  <Trans>Section</Trans>
                </Text>
                <Text>
                  {localFieldsOrder?.length ?? 0} <Trans>field(s)</Trans>
                </Text>
              </HStack>
              <HStack spacing={0}>
                <IconButton
                  as={AccordionButton}
                  aria-label={isExpanded ? t`Collapse section` : t`Expand section`}
                  icon={
                    <Icon
                      as={isExpanded ? ChevronUp : ChevronDown}
                      boxSize="16px"
                      color="fg.default"
                    />
                  }
                  size="sm"
                  variant="ghost"
                  p={0}
                />
                <Tooltip label={t`Edit section`} placement="top">
                  <IconButton
                    aria-label={t`Edit section`}
                    icon={<Icon as={Pencil} boxSize="16px" />}
                    size="sm"
                    variant="ghost"
                    onClick={handleEditSection}
                  />
                </Tooltip>

                <Tooltip label={t`Delete section`} placement="top">
                  <IconButton
                    aria-label={t`Delete section`}
                    icon={<Icon as={Trash} boxSize="16px" />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={handleDeleteSection}
                  />
                </Tooltip>

                <Tooltip label={t`Drag to reorder`} placement="top">
                  <IconButton
                    icon={<Icon as={GripVerticalIcon} boxSize="16px" />}
                    aria-label={t`Reorder option`}
                    size="sm"
                    variant="ghost"
                    cursor="grab"
                    onPointerDown={(e) => {
                      sectionDragControls.start(e);
                      onDragStart();
                    }}
                  />
                </Tooltip>
              </HStack>
            </HStack>

            <AccordionPanel p={5} pt={2}>
              <FieldList
                values={localFieldsOrder}
                onReorder={handleLocalReorder}
                listStyleType="none"
                mb={4}
              >
                {localFieldsOrder.map((field) => (
                  <FormField
                    key={field.id}
                    field={field}
                    sectionId={section.id}
                    setCurrentSlide={() => setCurrentSlide(slide)}
                    onDragEnd={() => handleItemDragEnd(field.id)}
                  />
                ))}
              </FieldList>

              <Button
                leftIcon={<AddIcon boxSize="16px" />}
                variant="ghost"
                borderWidth={2}
                borderColor="colors.borderEmphasis"
                borderStyle="dashed"
                size="md"
                py="22px"
                w="full"
                onClick={handleAddField}
              >
                <Trans>Add Field</Trans>
              </Button>
            </AccordionPanel>
          </Box>
        )}
      </AccordionItem>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCancelDelete}
        title={<Trans>Delete Form Section</Trans>}
        body={
          <Trans>
            You are about to delete this form section. This action cannot be undone. Are you sure
            you want to continue?
          </Trans>
        }
        onConfirm={handleConfirmDelete}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
      />
    </SectionItem>
  );
}

export function FormSectionAccordion({
  sections,
  slide,
  setCurrentSlide,
}: FormSectionAccordionProps) {
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
  const [localSectionsOrder, setLocalSectionsOrder] = useState<FormSectionType[]>(sections);
  const { newlyCreatedFormSectionId } = useSnapshot(unitAssessmentState);
  const utils = trpcAdmin.useUtils();

  const handleSectionsReorder = useCallback((newOrder: FormSectionType[]) => {
    setLocalSectionsOrder(newOrder);
  }, []);

  const reorderSectionsMutation = trpcAdmin.unitAssessment.reorderSection.useMutation({
    onSuccess: async () => {
      unitAssessmentState.setHasUnpublishedChanges(true);
      await utils.unitAssessment.getSlides.invalidate();
    },
  });

  const handleSectionDragEnd = useCallback(
    (sectionId: string) => {
      // Find the index of the dragged item in the updated list + 1 for position
      const targetIndex = localSectionsOrder.findIndex(({ id }) => id === sectionId);
      if (targetIndex !== -1) {
        reorderSectionsMutation.mutate({
          sectionId,
          formSlideId: slide.id,
          targetPosition: targetIndex + 1,
        });
      }
    },
    [localSectionsOrder, reorderSectionsMutation, slide.id],
  );

  // Expand newly added section
  useEffect(() => {
    if (newlyCreatedFormSectionId !== null && sections.length > 0) {
      const newSectionIndex = sections.findIndex(({ id }) => id === newlyCreatedFormSectionId);

      if (newSectionIndex !== -1) {
        setExpandedIndices([newSectionIndex]);
        unitAssessmentState.setNewlyCreatedFormSectionId(null);
      }
    }
  }, [newlyCreatedFormSectionId, sections]);

  // Update local state when sections change
  useEffect(() => {
    setLocalSectionsOrder(sections);
  }, [sections]);

  return (
    <Accordion
      allowMultiple
      index={expandedIndices}
      onChange={(indices) => setExpandedIndices(Array.isArray(indices) ? indices : [indices])}
    >
      <SectionList
        values={localSectionsOrder}
        onReorder={handleSectionsReorder}
        axis="y"
        listStyleType="none"
      >
        {localSectionsOrder.map((section) => (
          <Section
            key={section.id}
            section={section}
            slide={slide}
            setCurrentSlide={setCurrentSlide}
            onDragStart={() => setExpandedIndices([])}
            onDragEnd={() => handleSectionDragEnd(section.id)}
          />
        ))}
      </SectionList>
    </Accordion>
  );
}
